/* eslint-disable react/no-unescaped-entities */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import { API_ENDPOINTS } from '../../config/api';
import { services } from '../../services';
import InvestorApplicationsPanel from './InvestorApplicationsPanel';

const PresalePage: React.FC = () => {
  // Presale queue and investor applications share this tab
  const [view, setView] = useState<'presale' | 'investors'>('investors');
  const [presaleUsers, setPresaleUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [itemsPerPage] = useState(10);

  // Statistics state - removed unused variables

  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredUsers, setFilteredUsers] = useState<any[]>([]);

  useEffect(() => {
    fetchPresaleUsers();
  }, [currentPage]);

  // Filter users based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredUsers(presaleUsers);
    } else {
      const filtered = presaleUsers.filter((user: any) => {
        const fullName = user.fullName || user.name || '';
        const email = user.email || '';
        const walletAddress = user.walletAddress || '';
        const searchLower = searchTerm.toLowerCase();

        return (
          fullName.toLowerCase().includes(searchLower) ||
          email.toLowerCase().includes(searchLower) ||
          walletAddress.toLowerCase().includes(searchLower)
        );
      });
      setFilteredUsers(filtered);
    }
  }, [searchTerm, presaleUsers]);

  const fetchPresaleUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const apiUrl = `${API_ENDPOINTS.PRESALE.LIST}?page=${currentPage}&limit=${itemsPerPage}`;
      console.log('Fetching presale users from:', apiUrl);
      const response = await services.get(apiUrl);

      // Debug: Log the response to understand the structure
      console.log('Presale API Response:', response);
      console.log('Response type:', typeof response);
      console.log('Response.data type:', typeof response?.data);
      console.log('Response.data is array:', Array.isArray(response?.data));

      // Handle different response formats
      let users = [];
      if (response && response.data) {
        if (Array.isArray(response.data)) {
          console.log('Using response.data directly');
          users = response.data;
        } else if (Array.isArray(response.data.users)) {
          console.log('Using response.data.users');
          users = response.data.users;
        } else if (Array.isArray(response.data.data)) {
          console.log('Using response.data.data');
          users = response.data.data;
        } else if (response.data.results && Array.isArray(response.data.results)) {
          console.log('Using response.data.results');
          users = response.data.results;
        } else {
          console.warn('Unexpected API response format:', response.data);
          console.log('Available keys in response.data:', Object.keys(response.data || {}));

          // Try to find any array in the response
          const findArrayInObject = (obj: any): any[] => {
            if (Array.isArray(obj)) return obj;
            if (typeof obj === 'object' && obj !== null) {
              for (const key in obj) {
                if (Array.isArray(obj[key])) {
                  console.log(`Found array in key: ${key}`);
                  return obj[key];
                }
              }
            }
            return [];
          };

          users = findArrayInObject(response.data);
        }
      } else {
        console.warn('No response or response.data found');
        console.log('Full response object:', response);
      }

      console.log('Final users array:', users);
      console.log('Users count:', users.length);
      // Log first user's keys to see available fields
      if (users.length > 0) {
        console.log('First user object:', users[0]);
        console.log('First user keys:', Object.keys(users[0]));
      }

      // Extract pagination information from response
      if (response.data) {
        // Handle your specific API response format
        setTotalPages(
          response.data.pages || Math.ceil((response.data.total || users.length) / itemsPerPage)
        );
        setTotalUsers(response.data.total || users.length);

        // Calculate statistics from the current page data (removed unused calculations)

        console.log('API Response:', response.data);
        console.log('Total users:', response.data.total);
      }

      // If no users found, add some test data for debugging
      if (users.length === 0) {
        console.log('No users found, adding test data for debugging');
        const testData = [
          {
            _id: 'test1',
            fullName: 'John Doe',
            email: 'john@example.com',
            walletAddress: '0x1234567890abcdef1234567890abcdef12345678',
            reference: 'REF001',
            status: 'active',
            cryptoExperience: 'Intermediate',
            investmentRange: '$1,000 - $5,000',
            sourceOfFunds: 'Salary',
            riskTolerance: 'Medium',
            address: '123 Main St',
            country: 'USA',
            city: 'New York',
            createdAt: new Date().toISOString(),
          },
          {
            _id: 'test2',
            fullName: 'Jane Smith',
            email: 'jane@example.com',
            walletAddress: '0xabcdef1234567890abcdef1234567890abcdef12',
            reference: 'REF002',
            status: 'pending',
            cryptoExperience: 'Beginner',
            investmentRange: '$500 - $1,000',
            sourceOfFunds: 'Savings',
            riskTolerance: 'Low',
            address: '456 Oak Ave',
            country: 'Canada',
            city: 'Toronto',
            createdAt: new Date().toISOString(),
          },
        ];
        setPresaleUsers(testData);
        setTotalPages(1);
        setTotalUsers(testData.length);
        // Removed unused statistics calculations
      } else {
        setPresaleUsers(users);
      }
    } catch (err: any) {
      console.error('Error fetching presale users:', err);
      setError(err.response?.data?.message || 'Failed to fetch presale users');
      setPresaleUsers([]); // Ensure it's always an array
    } finally {
      setLoading(false);
    }
  };

  const handleViewUser = (user: any) => {
    setSelectedUser(user);
    setShowViewModal(true);
  };

  const closeViewModal = () => {
    setShowViewModal(false);
    setSelectedUser(null);
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const renderViewSwitch = () => (
    <div className='flex gap-2 mb-8'>
      {(
        [
          ['investors', 'Investor'],
          ['presale', 'Presale Users'],
        ] as const
      ).map(([key, label]) => (
        <button
          key={key}
          onClick={() => setView(key)}
          className='px-5 py-2 rounded-lg font-work-sans text-sm transition-all duration-200'
          style={{
            backgroundColor: view === key ? '#bbe425' : '#0B222C',
            color: view === key ? '#0B222C' : '#ede8c5',
            border: '1px solid #095D52',
          }}
        >
          {label}
        </button>
      ))}
    </div>
  );

  // Rendered before the presale fetch states so a presale error can't trap the tab
  if (view === 'investors') {
    return (
      <div>
        {renderViewSwitch()}
        <InvestorApplicationsPanel />
      </div>
    );
  }

  if (loading) {
    return (
      <div>
        {renderViewSwitch()}
        <div className='flex items-center justify-center h-64'>
          <div className='text-white text-lg'>Loading presale users...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        {renderViewSwitch()}
        <div className='flex items-center justify-center h-64'>
          <div className='text-red-400 text-lg'>{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {renderViewSwitch()}
      {/* Header with Total Users */}
      <div className='flex flex-col md:flex-row md:items-center md:justify-between mb-8'>
        <div>
          <h1
            className='text-3xl font-bold mb-2 font-dm-serif'
            style={{
              color: '#ede8c5',
              textShadow: '0 2px 6px rgba(0, 0, 0, 0.4), 0 0 10px rgba(187, 228, 37, 0.2)',
              letterSpacing: '0.5px',
            }}
          >
            Presale Users
          </h1>
          <p className='font-work-sans' style={{ color: '#ede8c5' }}>
            Manage and view all presale user registrations
          </p>
        </div>
      </div>

      {/* Search Filter */}
      <div className='mb-8'>
        <div
          className='rounded-lg p-6 shadow-lg transition-all duration-300'
          style={{
            backgroundColor: '#0B222C',
            border: '2px solid #095D52',
            boxShadow:
              '0 8px 25px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(187, 228, 37, 0.2), inset 0 1px 0 rgba(187, 228, 37, 0.1)',
            backgroundImage:
              'linear-gradient(135deg, rgba(187, 228, 37, 0.03) 0%, rgba(9, 93, 82, 0.05) 50%, rgba(11, 34, 44, 0.98) 100%)',
          }}
        >
          <div className='flex items-center space-x-4'>
            <div className='flex-1'>
              <label
                htmlFor='search'
                className='block text-sm font-medium mb-2 font-work-sans'
                style={{ color: '#ede8c5' }}
              >
                Search Users
              </label>
              <input
                type='text'
                id='search'
                placeholder='Search by name, email, or wallet address...'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className='w-full px-4 py-2 rounded-md focus:outline-none focus:ring-2 focus:border-transparent font-work-sans'
                style={{
                  backgroundColor: '#0B222C',
                  border: '1px solid #095D52',
                  color: '#ede8c5',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(187, 228, 37, 0.05)',
                }}
              />
            </div>
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className='px-4 py-2 rounded-lg transition-all duration-300 hover:scale-105 font-work-sans'
                style={{
                  backgroundColor: '#095D52',
                  color: '#ede8c5',
                  border: '1px solid #095D52',
                  boxShadow: '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)',
                  backgroundImage:
                    'linear-gradient(135deg, rgba(187, 228, 37, 0.1) 0%, rgba(9, 93, 82, 0.8) 100%)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#bbe425';
                  e.currentTarget.style.boxShadow =
                    '0 6px 20px rgba(187, 228, 37, 0.5), 0 0 0 1px rgba(187, 228, 37, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#095D52';
                  e.currentTarget.style.boxShadow =
                    '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)';
                }}
              >
                Clear
              </button>
            )}
          </div>
          {searchTerm && (
            <div className='mt-2 text-sm font-work-sans' style={{ color: '#ede8c5' }}>
              Found {filteredUsers.length} user(s) matching "{searchTerm}"
            </div>
          )}
        </div>
      </div>

      {/* Presale Users Table */}
      <div
        className='rounded-lg border shadow-lg transition-all duration-300'
        style={{
          backgroundColor: '#0B222C',
          border: '2px solid #095D52',
          boxShadow:
            '0 8px 25px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(187, 228, 37, 0.2), inset 0 1px 0 rgba(187, 228, 37, 0.1)',
          backgroundImage:
            'linear-gradient(135deg, rgba(187, 228, 37, 0.03) 0%, rgba(9, 93, 82, 0.05) 50%, rgba(11, 34, 44, 0.98) 100%)',
        }}
      >
        <div
          className='flex items-center justify-between p-6 border-b'
          style={{ borderColor: '#095D52' }}
        >
          <div>
            <h2
              className='text-2xl font-bold font-dm-serif'
              style={{
                color: '#ede8c5',
                textShadow: '0 2px 8px rgba(0, 0, 0, 0.5), 0 0 15px rgba(187, 228, 37, 0.3)',
                letterSpacing: '0.8px',
              }}
            >
              Presale Users Directory
            </h2>
            <p className='mt-1 font-work-sans' style={{ color: '#ede8c5' }}>
              View and manage all presale user registrations
            </p>
          </div>
          <div className='flex items-center space-x-4'>
            <div className='text-sm font-work-sans' style={{ color: '#ede8c5' }}>
              {filteredUsers.length} of {totalUsers} users
            </div>
            <div className='w-2 h-2 rounded-full' style={{ backgroundColor: '#4ade80' }}></div>
          </div>
        </div>

        <div className='overflow-x-auto'>
          <table className='w-full'>
            <thead
              style={{
                backgroundColor: '#095D52',
                backgroundImage:
                  'linear-gradient(135deg, rgba(187, 228, 37, 0.1) 0%, rgba(9, 93, 82, 0.8) 100%)',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
              }}
            >
              <tr>
                <th
                  className='px-6 py-4 text-left text-sm font-bold uppercase tracking-wider font-work-sans'
                  style={{
                    color: '#ede8c5',
                    textShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
                    letterSpacing: '0.5px',
                  }}
                >
                  User Info
                </th>
                <th
                  className='px-6 py-4 text-left text-sm font-bold uppercase tracking-wider font-work-sans'
                  style={{
                    color: '#ede8c5',
                    textShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
                    letterSpacing: '0.5px',
                  }}
                >
                  Email
                </th>
                <th
                  className='px-6 py-4 text-left text-sm font-bold uppercase tracking-wider font-work-sans'
                  style={{
                    color: '#ede8c5',
                    textShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
                    letterSpacing: '0.5px',
                  }}
                >
                  Wallet Address
                </th>
                <th
                  className='px-6 py-4 text-left text-sm font-bold uppercase tracking-wider font-work-sans'
                  style={{
                    color: '#ede8c5',
                    textShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
                    letterSpacing: '0.5px',
                  }}
                >
                  Reference
                </th>
                <th
                  className='px-6 py-4 text-left text-sm font-bold uppercase tracking-wider font-work-sans'
                  style={{
                    color: '#ede8c5',
                    textShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
                    letterSpacing: '0.5px',
                  }}
                >
                  Registration Date
                </th>
                <th
                  className='px-6 py-4 text-center text-sm font-bold uppercase tracking-wider font-work-sans'
                  style={{
                    color: '#ede8c5',
                    textShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
                    letterSpacing: '0.5px',
                  }}
                >
                  Actions
                </th>
                {/* <th
                  className='px-6 py-4 text-left text-sm font-bold uppercase tracking-wider font-work-sans'
                  style={{
                    color: '#ede8c5',
                    textShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
                    letterSpacing: '0.5px',
                  }}
                >
                  Status
                </th> */}
              </tr>
            </thead>
            <tbody
              style={{
                backgroundColor: '#0B222C',
                backgroundImage:
                  'linear-gradient(135deg, rgba(187, 228, 37, 0.03) 0%, rgba(9, 93, 82, 0.05) 50%, rgba(11, 34, 44, 0.98) 100%)',
              }}
              className='divide-y divide-gray-700'
            >
              {!Array.isArray(filteredUsers) || filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={6} className='px-6 py-12 text-center font-work-sans' style={{ color: '#ede8c5' }}>
                    {searchTerm
                      ? `No users found matching "${searchTerm}"`
                      : 'No presale users found'}
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user: any, index: number) => (
                  <tr
                    key={user._id || index}
                    className='transition-colors'
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#095D52')}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                  >
                    <td
                      className='px-6 py-4 whitespace-nowrap text-sm'
                      style={{ color: '#ede8c5' }}
                    >
                      {user.fullName ||
                        (user.firstName && user.lastName
                          ? `${user.firstName} ${user.lastName}`
                          : user.name || 'N/A')}
                    </td>
                    <td
                      className='px-6 py-4 whitespace-nowrap text-sm'
                      style={{ color: '#ede8c5' }}
                    >
                      {user.email || 'N/A'}
                    </td>
                    <td
                      className='px-6 py-4 whitespace-nowrap text-sm'
                      style={{ color: '#ede8c5' }}
                    >
                      {user.walletAddress
                        ? `${user.walletAddress.substring(0, 6)}...${user.walletAddress.substring(user.walletAddress.length - 4)}`
                        : user.wallet || 'N/A'}
                    </td>
                    <td
                      className='px-6 py-4 whitespace-nowrap text-sm'
                      style={{ color: '#ede8c5' }}
                    >
                      {user.reference || user.ref || 'N/A'}
                    </td>
                    <td
                      className='px-6 py-4 whitespace-nowrap text-sm'
                      style={{ color: '#ede8c5' }}
                    >
                      {user.createdAt
                        ? new Date(user.createdAt).toLocaleDateString()
                        : user.registrationDate
                          ? new Date(user.registrationDate).toLocaleDateString()
                          : user.submittedAt
                            ? new Date(user.submittedAt).toLocaleDateString()
                            : 'N/A'}
                    </td>
                    <td className='px-6 py-4 whitespace-nowrap text-center'>
                      <button
                        onClick={() => handleViewUser(user)}
                        className='p-2 rounded-lg transition-all duration-300 hover:scale-105'
                        style={{
                          backgroundColor: '#0B222C',
                          border: '1px solid #095D52',
                          color: '#ede8c5',
                          boxShadow:
                            '0 2px 8px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(187, 228, 37, 0.1)',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#095D52';
                          e.currentTarget.style.borderColor = '#bbe425';
                          e.currentTarget.style.boxShadow =
                            '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#0B222C';
                          e.currentTarget.style.borderColor = '#095D52';
                          e.currentTarget.style.boxShadow =
                            '0 2px 8px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(187, 228, 37, 0.1)';
                        }}
                        title='View Details'
                      >
                        <svg className='w-4 h-4' fill='currentColor' viewBox='0 0 20 20'>
                          <path d='M10 12a2 2 0 100-4 2 2 0 000 4z' />
                          <path
                            fillRule='evenodd'
                            d='M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z'
                            clipRule='evenodd'
                          />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        <div className='p-6 border-t' style={{ borderColor: '#465705' }}>
          <div className='flex items-center justify-between'>
            <div className='text-sm' style={{ color: '#ede8c5' }}>
              {searchTerm
                ? `Showing ${filteredUsers.length} of ${totalUsers} users • Filtered by "${searchTerm}"`
                : `Page ${currentPage} of ${totalPages} • Showing ${(currentPage - 1) * itemsPerPage + 1} to ${Math.min(currentPage * itemsPerPage, totalUsers)} of ${totalUsers} users`}
            </div>

            {!searchTerm && (
              <div className='flex items-center space-x-3'>
                {/* Previous Page */}
                <button
                  onClick={goToPreviousPage}
                  disabled={currentPage === 1}
                  className='px-4 py-2 text-sm font-medium rounded-md transition-all duration-300 hover:scale-105 flex items-center space-x-2 font-work-sans'
                  style={{
                    backgroundColor: currentPage === 1 ? '#095D5250' : '#095D52',
                    color: '#ede8c5',
                    border: '1px solid #095D52',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    boxShadow:
                      currentPage === 1
                        ? 'none'
                        : '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)',
                    backgroundImage:
                      currentPage === 1
                        ? 'none'
                        : 'linear-gradient(135deg, rgba(187, 228, 37, 0.1) 0%, rgba(9, 93, 82, 0.8) 100%)',
                  }}
                  onMouseEnter={(e) => {
                    if (currentPage !== 1) {
                      e.currentTarget.style.backgroundColor = '#bbe425';
                      e.currentTarget.style.boxShadow =
                        '0 6px 20px rgba(187, 228, 37, 0.5), 0 0 0 1px rgba(187, 228, 37, 0.4)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (currentPage !== 1) {
                      e.currentTarget.style.backgroundColor = '#095D52';
                      e.currentTarget.style.boxShadow =
                        '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)';
                    }
                  }}
                >
                  <svg className='w-4 h-4' fill='currentColor' viewBox='0 0 20 20'>
                    <path
                      fillRule='evenodd'
                      d='M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z'
                      clipRule='evenodd'
                    />
                  </svg>
                  <span>Previous</span>
                </button>

                {/* Next Page */}
                <button
                  onClick={goToNextPage}
                  disabled={currentPage === totalPages}
                  className='px-4 py-2 text-sm font-medium rounded-md transition-all duration-300 hover:scale-105 flex items-center space-x-2 font-work-sans'
                  style={{
                    backgroundColor: currentPage === totalPages ? '#095D5250' : '#095D52',
                    color: '#ede8c5',
                    border: '1px solid #095D52',
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                    boxShadow:
                      currentPage === totalPages
                        ? 'none'
                        : '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)',
                    backgroundImage:
                      currentPage === totalPages
                        ? 'none'
                        : 'linear-gradient(135deg, rgba(187, 228, 37, 0.1) 0%, rgba(9, 93, 82, 0.8) 100%)',
                  }}
                  onMouseEnter={(e) => {
                    if (currentPage !== totalPages) {
                      e.currentTarget.style.backgroundColor = '#bbe425';
                      e.currentTarget.style.boxShadow =
                        '0 6px 20px rgba(187, 228, 37, 0.5), 0 0 0 1px rgba(187, 228, 37, 0.4)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (currentPage !== totalPages) {
                      e.currentTarget.style.backgroundColor = '#095D52';
                      e.currentTarget.style.boxShadow =
                        '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)';
                    }
                  }}
                >
                  <span>Next</span>
                  <svg className='w-4 h-4' fill='currentColor' viewBox='0 0 20 20'>
                    <path
                      fillRule='evenodd'
                      d='M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z'
                      clipRule='evenodd'
                    />
                  </svg>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* View User Details Modal */}
      {showViewModal && selectedUser && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'>
          <div
            className='rounded-xl border max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto shadow-lg'
            style={{
              backgroundColor: '#0B222C',
              borderColor: '#095D52',
              boxShadow:
                '0 8px 25px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(187, 228, 37, 0.2), inset 0 1px 0 rgba(187, 228, 37, 0.1)',
              backgroundImage:
                'linear-gradient(135deg, rgba(187, 228, 37, 0.03) 0%, rgba(9, 93, 82, 0.05) 50%, rgba(11, 34, 44, 0.98) 100%)',
            }}
          >
            <div className='p-6'>
              <div className='flex justify-between items-center mb-6'>
                <h2
                  className='text-2xl font-bold font-dm-serif'
                  style={{
                    color: '#ede8c5',
                    textShadow: '0 2px 8px rgba(0, 0, 0, 0.5), 0 0 15px rgba(187, 228, 37, 0.3)',
                    letterSpacing: '0.8px',
                  }}
                >
                  User Details
                </h2>
                <button
                  onClick={closeViewModal}
                  className='text-2xl transition-all duration-300 hover:scale-110'
                  style={{ color: '#ede8c5' }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = '#bbe425')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = '#ede8c5')}
                >
                  ×
                </button>
              </div>

              <div className='space-y-6'>
                {/* Basic Information */}
                <div
                  className='rounded-lg p-4'
                  style={{
                    backgroundColor: '#0B222C',
                    border: '1px solid #095D52',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(187, 228, 37, 0.1)',
                  }}
                >
                  <h3
                    className='text-lg font-semibold font-dm-serif mb-4'
                    style={{ color: '#ede8c5' }}
                  >
                    Basic Information
                  </h3>
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                    <div>
                      <label
                        className='block text-sm font-medium font-work-sans mb-1'
                        style={{ color: '#ede8c5' }}
                      >
                        Full Name
                      </label>
                      <p className='font-work-sans' style={{ color: '#ede8c5' }}>
                        {selectedUser.fullName ||
                          (selectedUser.firstName && selectedUser.lastName
                            ? `${selectedUser.firstName} ${selectedUser.lastName}`
                            : selectedUser.name || 'N/A')}
                      </p>
                    </div>
                    <div>
                      <label
                        className='block text-sm font-medium font-work-sans mb-1'
                        style={{ color: '#ede8c5' }}
                      >
                        Email
                      </label>
                      <p className='font-work-sans' style={{ color: '#ede8c5' }}>
                        {selectedUser.email || 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Wallet Information */}
                <div
                  className='rounded-lg p-4'
                  style={{
                    backgroundColor: '#0B222C',
                    border: '1px solid #095D52',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(187, 228, 37, 0.1)',
                  }}
                >
                  <h3
                    className='text-lg font-semibold font-dm-serif mb-4'
                    style={{ color: '#ede8c5' }}
                  >
                    Wallet Information
                  </h3>
                  <div>
                    <div>
                      <label
                        className='block text-sm font-medium font-work-sans mb-1'
                        style={{ color: '#ede8c5' }}
                      >
                        Wallet Address
                      </label>
                      <p className='font-work-sans break-all' style={{ color: '#ede8c5' }}>
                        {selectedUser.walletAddress || selectedUser.wallet || 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Presale Information */}
                <div
                  className='rounded-lg p-4'
                  style={{
                    backgroundColor: '#0B222C',
                    border: '1px solid #095D52',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(187, 228, 37, 0.1)',
                  }}
                >
                  <h3
                    className='text-lg font-semibold font-dm-serif mb-4'
                    style={{ color: '#ede8c5' }}
                  >
                    Presale Information
                  </h3>
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                    <div>
                      <label className='block text-sm font-medium text-gray-300 mb-1'>
                        Reference
                      </label>
                      <p className='text-white'>
                        {selectedUser.reference || selectedUser.ref || 'N/A'}
                      </p>
                    </div>
                    {/* <div>
                      <label className='block text-sm font-medium text-gray-300 mb-1'>Status</label>
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          selectedUser.status === 'active' || selectedUser.isActive
                            ? 'bg-green-100 text-green-800'
                            : selectedUser.status === 'pending' || !selectedUser.isVerified
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {selectedUser.status || (selectedUser.isActive ? 'Active' : 'Pending')}
                      </span>
                    </div> */}
                    {selectedUser.presaleTier && (
                      <div>
                        <label className='block text-sm font-medium text-gray-300 mb-1'>
                          Presale Tier
                        </label>
                        <p className='text-white'>{selectedUser.presaleTier}</p>
                      </div>
                    )}
                    {selectedUser.allocation && (
                      <div>
                        <label className='block text-sm font-medium text-gray-300 mb-1'>
                          Allocation
                        </label>
                        <p className='text-white'>{selectedUser.allocation}</p>
                      </div>
                    )}
                    {selectedUser.cryptoExperience && (
                      <div>
                        <label className='block text-sm font-medium text-gray-300 mb-1'>
                          Crypto Experience
                        </label>
                        <p className='text-white'>{selectedUser.cryptoExperience}</p>
                      </div>
                    )}
                    {selectedUser.investmentRange && (
                      <div>
                        <label className='block text-sm font-medium text-gray-300 mb-1'>
                          Investment Range
                        </label>
                        <p className='text-white'>{selectedUser.investmentRange}</p>
                      </div>
                    )}
                    {selectedUser.sourceOfFunds && (
                      <div>
                        <label className='block text-sm font-medium text-gray-300 mb-1'>
                          Source of Funds
                        </label>
                        <p className='text-white'>{selectedUser.sourceOfFunds}</p>
                      </div>
                    )}
                    {selectedUser.riskTolerance && (
                      <div>
                        <label className='block text-sm font-medium text-gray-300 mb-1'>
                          Risk Tolerance
                        </label>
                        <p className='text-white'>{selectedUser.riskTolerance}</p>
                      </div>
                    )}
                    {selectedUser.address && (
                      <div>
                        <label className='block text-sm font-medium text-gray-300 mb-1'>
                          Address
                        </label>
                        <p className='text-white'>{selectedUser.address}</p>
                      </div>
                    )}
                    {selectedUser.country && (
                      <div>
                        <label className='block text-sm font-medium text-gray-300 mb-1'>
                          Country
                        </label>
                        <p className='text-white'>{selectedUser.country}</p>
                      </div>
                    )}
                    {selectedUser.state && (
                      <div>
                        <label className='block text-sm font-medium text-gray-300 mb-1'>
                          State/Province
                        </label>
                        <p className='text-white'>{selectedUser.state}</p>
                      </div>
                    )}
                    {selectedUser.city && (
                      <div>
                        <label className='block text-sm font-medium text-gray-300 mb-1'>City</label>
                        <p className='text-white'>{selectedUser.city}</p>
                      </div>
                    )}
                    {selectedUser.zipCode && (
                      <div>
                        <label className='block text-sm font-medium text-gray-300 mb-1'>
                          ZIP/Postal Code
                        </label>
                        <p className='text-white'>{selectedUser.zipCode}</p>
                      </div>
                    )}
                    {selectedUser.notes && (
                      <div className='md:col-span-2'>
                        <label className='block text-sm font-medium text-gray-300 mb-1'>
                          Notes
                        </label>
                        <p className='text-white'>{selectedUser.notes}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Investment Information */}
                {(selectedUser.investmentAmount ||
                  selectedUser.contributionAmount ||
                  selectedUser.tokenAmount ||
                  selectedUser.purchaseDate) && (
                  <div
                    className='rounded-lg p-4'
                    style={{
                      backgroundColor: '#0B222C',
                      border: '1px solid #095D52',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(187, 228, 37, 0.1)',
                    }}
                  >
                    <h3
                      className='text-lg font-semibold font-dm-serif mb-4'
                      style={{ color: '#ede8c5' }}
                    >
                      Investment Information
                    </h3>
                    <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                      {selectedUser.investmentAmount && (
                        <div>
                          <label className='block text-sm font-medium text-gray-300 mb-1'>
                            Investment Amount
                          </label>
                          <p className='text-white'>{selectedUser.investmentAmount}</p>
                        </div>
                      )}
                      {selectedUser.contributionAmount && (
                        <div>
                          <label className='block text-sm font-medium text-gray-300 mb-1'>
                            Contribution Amount
                          </label>
                          <p className='text-white'>{selectedUser.contributionAmount}</p>
                        </div>
                      )}
                      {selectedUser.tokenAmount && (
                        <div>
                          <label className='block text-sm font-medium text-gray-300 mb-1'>
                            Token Amount
                          </label>
                          <p className='text-white'>{selectedUser.tokenAmount}</p>
                        </div>
                      )}
                      {selectedUser.purchaseDate && (
                        <div>
                          <label className='block text-sm font-medium text-gray-300 mb-1'>
                            Purchase Date
                          </label>
                          <p className='text-white'>
                            {new Date(selectedUser.purchaseDate).toLocaleDateString()}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* KYC Information */}
                {(selectedUser.kycStatus ||
                  selectedUser.verificationStatus ||
                  selectedUser.documentType ||
                  selectedUser.idNumber) && (
                  <div
                    className='rounded-lg p-4'
                    style={{
                      backgroundColor: '#0B222C',
                      border: '1px solid #095D52',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(187, 228, 37, 0.1)',
                    }}
                  >
                    <h3
                      className='text-lg font-semibold font-dm-serif mb-4'
                      style={{ color: '#ede8c5' }}
                    >
                      KYC Information
                    </h3>
                    <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                      {selectedUser.kycStatus && (
                        <div>
                          <label className='block text-sm font-medium text-gray-300 mb-1'>
                            KYC Status
                          </label>
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              selectedUser.kycStatus === 'verified' ||
                              selectedUser.kycStatus === 'approved'
                                ? 'bg-green-100 text-green-800'
                                : selectedUser.kycStatus === 'pending'
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {selectedUser.kycStatus}
                          </span>
                        </div>
                      )}
                      {selectedUser.verificationStatus && (
                        <div>
                          <label className='block text-sm font-medium text-gray-300 mb-1'>
                            Verification Status
                          </label>
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              selectedUser.verificationStatus === 'verified'
                                ? 'bg-green-100 text-green-800'
                                : selectedUser.verificationStatus === 'pending'
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {selectedUser.verificationStatus}
                          </span>
                        </div>
                      )}
                      {selectedUser.documentType && (
                        <div>
                          <label className='block text-sm font-medium text-gray-300 mb-1'>
                            Document Type
                          </label>
                          <p className='text-white'>{selectedUser.documentType}</p>
                        </div>
                      )}
                      {selectedUser.idNumber && (
                        <div>
                          <label className='block text-sm font-medium text-gray-300 mb-1'>
                            ID Number
                          </label>
                          <p className='text-white font-mono text-sm'>{selectedUser.idNumber}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Social Information */}
                {(selectedUser.telegram ||
                  selectedUser.twitter ||
                  selectedUser.discord ||
                  selectedUser.linkedin) && (
                  <div
                    className='rounded-lg p-4'
                    style={{
                      backgroundColor: '#0B222C',
                      border: '1px solid #095D52',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(187, 228, 37, 0.1)',
                    }}
                  >
                    <h3
                      className='text-lg font-semibold font-dm-serif mb-4'
                      style={{ color: '#ede8c5' }}
                    >
                      Social Information
                    </h3>
                    <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                      {selectedUser.telegram && (
                        <div>
                          <label className='block text-sm font-medium text-gray-300 mb-1'>
                            Telegram
                          </label>
                          <p className='text-white'>{selectedUser.telegram}</p>
                        </div>
                      )}
                      {selectedUser.twitter && (
                        <div>
                          <label className='block text-sm font-medium text-gray-300 mb-1'>
                            Twitter
                          </label>
                          <p className='text-white'>{selectedUser.twitter}</p>
                        </div>
                      )}
                      {selectedUser.discord && (
                        <div>
                          <label className='block text-sm font-medium text-gray-300 mb-1'>
                            Discord
                          </label>
                          <p className='text-white'>{selectedUser.discord}</p>
                        </div>
                      )}
                      {selectedUser.linkedin && (
                        <div>
                          <label className='block text-sm font-medium text-gray-300 mb-1'>
                            LinkedIn
                          </label>
                          <p className='text-white'>{selectedUser.linkedin}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* System Information */}
                <div
                  className='rounded-lg p-4'
                  style={{
                    backgroundColor: '#0B222C',
                    border: '1px solid #095D52',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(187, 228, 37, 0.1)',
                  }}
                >
                  <h3
                    className='text-lg font-semibold font-dm-serif mb-4'
                    style={{ color: '#ede8c5' }}
                  >
                    System Information
                  </h3>
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                    <div>
                      <label className='block text-sm font-medium text-gray-300 mb-1'>
                        User ID
                      </label>
                      <p className='text-white font-mono text-sm'>{selectedUser._id || 'N/A'}</p>
                    </div>
                    <div>
                      <label className='block text-sm font-medium text-gray-300 mb-1'>
                        Registration Date
                      </label>
                      <p className='text-white'>
                        {selectedUser.createdAt
                          ? new Date(selectedUser.createdAt).toLocaleDateString()
                          : selectedUser.registrationDate
                            ? new Date(selectedUser.registrationDate).toLocaleDateString()
                            : 'N/A'}
                      </p>
                    </div>
                    {selectedUser.updatedAt && (
                      <div>
                        <label className='block text-sm font-medium text-gray-300 mb-1'>
                          Last Updated
                        </label>
                        <p className='text-white'>
                          {new Date(selectedUser.updatedAt).toLocaleDateString()}
                        </p>
                      </div>
                    )}
                    {selectedUser.ipAddress && (
                      <div>
                        <label className='block text-sm font-medium text-gray-300 mb-1'>
                          IP Address
                        </label>
                        <p className='text-white font-mono text-sm'>{selectedUser.ipAddress}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className='flex justify-end mt-6'>
                <button
                  onClick={closeViewModal}
                  className='px-6 py-2 rounded-lg font-work-sans transition-all duration-300 hover:scale-105'
                  style={{
                    backgroundColor: '#095D52',
                    border: '1px solid #095D52',
                    color: '#ede8c5',
                    boxShadow: '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)',
                    backgroundImage:
                      'linear-gradient(135deg, rgba(187, 228, 37, 0.1) 0%, rgba(9, 93, 82, 0.8) 100%)',
                  }}
                  onMouseEnter={(e) => {
                    const target = e.target as HTMLButtonElement;
                    target.style.backgroundColor = '#bbe425';
                    target.style.boxShadow =
                      '0 6px 20px rgba(187, 228, 37, 0.5), 0 0 0 1px rgba(187, 228, 37, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    const target = e.target as HTMLButtonElement;
                    target.style.backgroundColor = '#095D52';
                    target.style.boxShadow =
                      '0 4px 15px rgba(9, 93, 82, 0.4), 0 0 0 1px rgba(187, 228, 37, 0.3)';
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PresalePage;
